import { metaRepo } from './repositories/meta';

export interface PersistStatus {
  /** `null` when the API is unavailable, so "unknown" is distinguishable from "refused". */
  granted: boolean | null;
  quotaBytes: number | null;
  usageBytes: number | null;
  installedToHomeScreen: boolean | null;
}

/**
 * Claim persistent storage, on **every** launch (INV-04).
 *
 * Not once at install: the grant is heuristic, WebKit decides it from signals that
 * change over time, and Home Screen Web Apps are favoured when it is decided. So we
 * ask again each launch and record the answer.
 *
 * A `false` result is a real outcome, not an error. It means IndexedDB is evictable,
 * which means the export in Betreuer-Modus is the only durability guarantee left —
 * so it is recorded in `meta` and surfaced as a warning rather than swallowed.
 * Clearing Safari history can still wipe an installed PWA's data even when the grant
 * is `true`, which is why backup stays P0 (ADR-006) and why we are not trying to
 * engineer certainty here.
 */
export async function claimPersistentStorage(): Promise<PersistStatus> {
  const standalone =
    typeof window === 'undefined'
      ? null
      : window.matchMedia?.('(display-mode: standalone)').matches ?? null;

  const status: PersistStatus = {
    granted: null,
    quotaBytes: null,
    usageBytes: null,
    installedToHomeScreen: standalone,
  };

  try {
    if (navigator.storage?.persisted && navigator.storage.persist) {
      status.granted = (await navigator.storage.persisted()) || (await navigator.storage.persist());
    }
    if (navigator.storage?.estimate) {
      const estimate = await navigator.storage.estimate();
      status.quotaBytes = estimate.quota ?? null;
      status.usageBytes = estimate.usage ?? null;
    }
  } catch (err) {
    // A failed claim must never block startup — nothing may sit between the person
    // and a keyboard.
    console.error('[storage] persist() failed', err);
  }

  try {
    await metaRepo.update({
      persistGranted: status.granted,
      persistCheckedAt: new Date().toISOString(),
      installedToHomeScreen: status.installedToHomeScreen,
    });
  } catch (err) {
    console.error('[storage] could not record persist result', err);
  }

  if (status.granted === false) {
    console.warn('[storage] persistent storage NOT granted — data is evictable, backup is critical');
  }

  return status;
}
